import {TFunction, useTranslation} from 'react-i18next';
import {
  APIActions,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  HtmlTrans,
  MUIDataTable,
  Pill,
  Progress,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  TitleHeader,
  Typography,
  useDataTable,
} from 'web-library';
import React, {useEffect, useState} from 'react';
import {useTheme} from '@mui/material/styles';


import {makeStyles} from 'tss-react/mui';
import {SelectChangeEvent, useMediaQuery} from '@mui/material';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import EditButton from 'web-library/components/buttons/EditButton';
import DeleteButton from 'web-library/components/buttons/DeleteButton';
import {
  ANNOUNCEMENT_FUNCTION_MAP,
  generateAuthorityName,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import {stringToLongDateAndShortTime, stringToSemanticDisplay} from '../../utils/DateUtils';
import {useGetAnnouncementFeature} from '../../api-refactored/manage-announcements/useGetAnnouncementFeature';
import {configurationTypeData} from '../../modules/admin/logic/dropDownOption';
import {
  AnnouncementListingResponseContentObject,
  usePostAnnouncementListing,
} from '../../api-refactored/manage-announcements/usePostAnnouncementListing';
import {
  AnnouncementConfigResponse,
  useGetAnnouncementConfig,
} from '../../api-refactored/manage-announcements/useGetAnnouncementConfig';
import {useDeleteAnnouncement} from '../../api-refactored/manage-announcements/useDeleteAnnouncement';
import RouteKey from '../../routes/key';
import {useGetUserAuthorities} from '../../api-refactored/user/useGetUserAuthorities';

const useStyles = makeStyles()((theme) =>
    ({
      select: {
        marginBottom: theme.spacing(1.5),
      },
      headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        [theme.breakpoints.down('sm')]: {
          display: 'block',
          marginBottom: theme.spacing(3),
        },
      },
      button: {
        [theme.breakpoints.down('sm')]: {
          height: 'fit-content',
        },
      },
      dataTable: {
        marginBottom: theme.spacing(8),
      },
      titleDescription: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': '3',
        '-webkit-box-orient': 'vertical',
        maxWidth: '18ch',
        wordWrap: 'break-word',
        fontSize: '1rem',
      },
      updatedByText: {
        color: theme.color.text.textTertiary,
      },
      iconButtonContainer: {
        display: 'inline-flex',
      },
      editButtonContainer: {
        marginRight: theme.spacing(2),
        [theme.breakpoints.down(1827)]: {
          marginBottom: theme.spacing(3),
        },
      },
    }));

const {admin} = RouteKey.child;
const {
  manageAnnouncements,
  createNewAnnouncement,
  createNewBroadcastMessage,
  createNewFAQ,
  createNewAdvisoryBanner,
  createNewPaymentMessage,
  editAnnouncement,
  editBroadcastMessage,
  editFAQ,
  editAdvisoryBanner,
  editPaymentMessage,
} = RouteKey.admin.child;

const selectFeatureSubPath = (feature: string) => {
  if (feature === 'ANNOUNCEMENTS') {
    return createNewAnnouncement.route;
  }
  if (feature === 'BROADCAST_MESSAGE') {
    return createNewBroadcastMessage.route;
  }
  if (feature === 'FAQ') {
    return createNewFAQ.route;
  }
  if (feature === 'ADVISORY_BANNER') {
    return createNewAdvisoryBanner.route;
  }
  if (feature === 'PAYMENT_MESSAGE') {
    return createNewPaymentMessage.route;
  }
  return null;
};

const selectFeatureSubPathEditAnnouncement = (feature: string) => {
  if (feature === 'ANNOUNCEMENTS') {
    return editAnnouncement.route;
  }
  if (feature === 'BROADCAST_MESSAGE') {
    return editBroadcastMessage.route;
  }
  if (feature === 'FAQ') {
    return editFAQ.route;
  }
  if (feature === 'ADVISORY_BANNER') {
    return editAdvisoryBanner.route;
  }
  if (feature === 'PAYMENT_MESSAGE') {
    return editPaymentMessage.route;
  }
  return null;
};

export interface AnnouncementNotificationTitleUuid {
  title: string;
  uuid: string;
}

const displayPill = (data: AnnouncementListingResponseContentObject[], dataIndex: number) => {
  const {status} = data[dataIndex];
  if (status === 'Published') {
    return <Pill variant="positive" size="M" label={status}/>;
  }
  if (status === 'Upcoming') {
    return <Pill variant="info" size="M" label={status}/>;
  }
  if (status === 'Expired') {
    return <Pill variant="default" size="M" label={status}/>;
  }
  return <>-</>;
};

const displayDateTimeUpdated = (
    data: AnnouncementListingResponseContentObject[],
    dataIndex: number,
    classes: Record<string, string>
) => (
    <>
      {stringToLongDateAndShortTime(data[dataIndex].updateDateTime)}
      <br/>
      <Typography
          className={classes.updatedByText}
          variant="subtitle2"
      >{`Updated by ${data[dataIndex].updatedBy}`}</Typography>
    </>
);

const tableColumns = (
    featureValue: string,
    state: TableState,
    announcementConfig: AnnouncementConfigResponse,
    hooks: {
      classes: Record<string, string>;
      history: RouteComponentProps['history'];
      t: TFunction<'translation'>;
    },
    setDeleteAnnouncementTitleUuid: React.Dispatch<React.SetStateAction<AnnouncementNotificationTitleUuid>>,
    setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>,
    authorities?: string[]
) => [
  {
    name: 'title',
    label: hooks.t('announcementsNotifications->>Listing.Title'),
    options: {
      display: announcementConfig.titleEnabled,
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
          <Typography className={hooks.classes.titleDescription}>
            {state.data[dataIndex].title}
          </Typography>
      ),
    },
  },
  {
    name: 'description',
    label: hooks.t('announcementsNotifications->>Listing.Description'),
    options: {
      sort: false,
      display: announcementConfig.descriptionEnabled,
      customBodyRenderLite: (dataIndex: number) => (
          <Typography className={hooks.classes.titleDescription}>
            {state.data[dataIndex].description ?? '-'}
          </Typography>
      ),
    },
  },
  {
    name: 'priority',
    label: hooks.t('announcementsNotifications->>Listing.Priority'),
    options: {
      setCellProps: () => ({style: {width: '8%'}}),
      sort: false,
      display: announcementConfig.priorityEnabled,
      customBodyRenderLite: (dataIndex: number) => {
        const {priority} = state.data[dataIndex];
        if (priority) {
          return (
              <Pill
                  variant="warning"
                  label={hooks.t('announcementsNotifications->>Listing.Priority')}
              />
          );
        }
        return <>-</>;
      },
    },
  },
  {
    name: 'startDate',
    label: hooks.t('announcementsNotifications->>Listing.StartDate'),
    options: {
      sort: false,
      display: announcementConfig.validityPeriodEnabled,
      customBodyRenderLite: (dataIndex: number) => (
          <Typography noWrap>
            {stringToSemanticDisplay(state.data[dataIndex].startDate || '')}
          </Typography>
      ),
    },
  },
  {
    name: 'endDate',
    label: hooks.t('announcementsNotifications->>Listing.EndDate'),
    options: {
      sort: false,
      display: announcementConfig.validityPeriodEnabled,
      customBodyRenderLite: (dataIndex: number) => (
          <Typography noWrap>
            {stringToSemanticDisplay(state.data[dataIndex].endDate || '')}
          </Typography>
      ),
    },
  },
  {
    name: 'updatedAt',
    label: hooks.t('announcementsNotifications->>Listing.DateTimeUpdated'),
    options: {
      setCellProps: () => ({style: {width: '13%'}}),
      customBodyRenderLite: (dataIndex: number) =>
          displayDateTimeUpdated(state.data, dataIndex, hooks.classes),
    },
  },
  {
    name: 'status',
    label: hooks.t('announcementsNotifications->>Listing.Status'),
    options: {
      setCellProps: () => ({style: {width: '8%'}}),
      sort: false,
      customBodyRenderLite: (dataIndex: number) => displayPill(state.data, dataIndex),
    },
  },
  {
    name: 'actions',
    label: hooks.t('announcementsNotifications->>Listing.Actions'),
    options: {
      setCellProps: () => ({style: {paddingTop: '15px', width: '15%'}}),
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
          <>
            {authorities?.includes(
                generateAuthorityName(
                    'CP',
                    FunctionModule.ADMIN,
                    ANNOUNCEMENT_FUNCTION_MAP[featureValue],
                    PermissionType.EDIT
                )
            ) &&
            state.data[dataIndex].creator && (
                <Box
                    className={`${hooks.classes.iconButtonContainer} ${hooks.classes.editButtonContainer}`}
                >
                  <EditButton
                      editHandler={() =>
                          hooks.history.push(
                              `/${admin}/${manageAnnouncements.route}/${state.data[dataIndex].uuid
                              }/${selectFeatureSubPathEditAnnouncement(featureValue)}`,
                              {
                                featureValue,
                                page: state.page,
                                pageSize: state.size,
                              }
                          )
                      }
                      label={hooks.t('announcementsNotifications->>Listing.Edit')}
                      typographyVariant="h6"
                  />
                </Box>
            )}
            {authorities?.includes(
                generateAuthorityName(
                    'CP',
                    FunctionModule.ADMIN,
                    ANNOUNCEMENT_FUNCTION_MAP[featureValue],
                    PermissionType.DELETE
                )
            ) && (
                <Box className={hooks.classes.iconButtonContainer}>
                  <DeleteButton
                      deleteHandler={() => {
                        setDeleteAnnouncementTitleUuid({
                          title: state.data[dataIndex].title,
                          uuid: state.data[dataIndex].uuid,
                        });
                        setOpenDeleteModal(true);
                      }}
                      label={hooks.t('announcementsNotifications->>Listing.Delete')}
                      typographyVariant="h6"
                  />
                </Box>
            )}
          </>
      ),
    },
  },
];

const displaySectionHeaderLabel = (feature: string, t: TFunction<'translation'>) => {
  if (feature === 'ANNOUNCEMENTS') {
    return <SectionHeader title={t('announcementsNotifications->>Listing.AllAnnouncements')}/>;
  }
  if (feature === 'BROADCAST_MESSAGE') {
    return <SectionHeader title={t('announcementsNotifications->>Listing.AllBroadcastMessages')}/>;
  }
  if (feature === 'FAQ') {
    return <SectionHeader title={t('announcementsNotifications->>Listing.AllFaq')}/>;
  }
  if (feature === 'ADVISORY_BANNER') {
    return <SectionHeader title={t('announcementsNotifications->>Listing.AllAdvisoryBanner')}/>;
  }
  if (feature === 'PAYMENT_MESSAGE') {
    return <SectionHeader title={t('announcementsNotifications->>Listing.AllPaymentMessage')}/>;
  }
  return null;
};

const displayButtonLabel = (feature: string, t: TFunction<'translation'>) => {
  if (feature === 'ANNOUNCEMENTS') {
    return t('announcementsNotifications->>Listing.NewAnnouncement');
  }
  if (feature === 'BROADCAST_MESSAGE') {
    return t('announcementsNotifications->>Listing.NewBroadcastMessage');
  }
  if (feature === 'FAQ') {
    return t('announcementsNotifications->>Listing.NewFaq');
  }
  if (feature === 'ADVISORY_BANNER') {
    return t('announcementsNotifications->>Listing.NewAdvisoryBanner');
  }
  if (feature === 'PAYMENT_MESSAGE') {
    return t('announcementsNotifications->>Listing.NewPaymentMessage');
  }
  return null;
};

const ManageAnnouncementsListing = () => {
  const {t} = useTranslation();
  const {classes} = useStyles();

  const history = useHistory<{ featureValue: string; page?: number; pageSize?: number }>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const authorities = useGetUserAuthorities();

  const featureValueFromCreateAnnouncement = history?.location?.state?.featureValue;

  const [featureValue, setFeatureValue] = useState<string>(
      featureValueFromCreateAnnouncement || ''
  );

  const [featureDisplay, setFeatureDisplay] = useState<string>('');

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [
    deleteAnnouncementTitleUuid,
    setDeleteAnnouncementTitleUuid,
  ] = useState<AnnouncementNotificationTitleUuid>({
    title: '',
    uuid: '',
  });

  const {
    mutate: deleteAnnouncement,
    status: deleteAnnouncementStatus,
    reset: deleteAnnouncementReset,
  } = useDeleteAnnouncement({
    onSuccess: () => {
      handleCloseDeleteModal();
      deleteAnnouncementReset();
      mutatePostAnnouncementListing({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        announcementType: featureValue,
      });
    },
    uuid: deleteAnnouncementTitleUuid.uuid,
  });

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    deleteAnnouncement(featureValue);
  };

  const {
    data: announcementFeature,
    isLoading: isLoadingAnnouncementFeature,
  } = useGetAnnouncementFeature({
    onSuccess: (res) => {
      if (featureValue === '') {
        setFeatureValue(res[0].code);
      }
    },
  });

  const onFeatureChangedHandler = (
      event: SelectChangeEvent<unknown>
  ) => setFeatureValue(event.target.value as string);

  const dataFromEditAnnouncement = history?.location?.state;

  const {state, dispatch: tableDispatch, tOptions} = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: dataFromEditAnnouncement?.page || 0,
    size: dataFromEditAnnouncement?.pageSize || 5,
    sortColumn: 'updatedAt',
    order: 'desc',
    search: featureValue,
  });

  const {
    data: announcementConfig,
    isLoading: isLoadingAnnouncementConfig,
  } = useGetAnnouncementConfig({
    announcementType: featureValue,
    enabled: !!featureValue,
  });

  const {
    mutate: mutatePostAnnouncementListing,
    isLoading: isLoadingAnnouncementListing,
  } = usePostAnnouncementListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    if (featureValue) {
      mutatePostAnnouncementListing({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        announcementType: featureValue,
      });
    }
  }, [
    mutatePostAnnouncementListing,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    featureValue,
  ]);

  useEffect(() => {
    if (featureValue === 'ANNOUNCEMENTS') {
      setFeatureDisplay('Announcements');
    }
    if (featureValue === 'BROADCAST_MESSAGE') {
      setFeatureDisplay('Broadcast Message');
    }
    if (featureValue === 'FAQ') {
      setFeatureDisplay('FAQ');
    }
    if (featureValue === 'ADVISORY_BANNER') {
      setFeatureDisplay('Advisory Banner');
    }
    if (featureValue === 'PAYMENT_MESSAGE') {
      setFeatureDisplay('Payment Message');
    }
  }, [featureValue]);

  if (
      isLoadingAnnouncementFeature ||
      isLoadingAnnouncementListing ||
      isLoadingAnnouncementConfig ||
      !featureValue
  ) {
    return (
        <ContentTemplate>
          <Progress color="secondary"/>
        </ContentTemplate>
    );
  }

  const handleCreateNewAnnouncement = () =>
      history.push(`/${admin}/${manageAnnouncements.route}/${selectFeatureSubPath(featureValue)}`, {
        featureValue,
      });

  return (
      <>
        <TitleHeader title={t('announcementsNotifications->>Listing.ManageAnnouncements')}/>
        <HeaderTemplate>
          <HeaderInput
              label={t('announcementsNotifications->>Listing.Feature')}
              input={
                announcementFeature &&
                configurationTypeData(announcementFeature) && (
                    <CustomSelect
                        id="inputSelect-feature"
                        isHeader
                        menuItems={configurationTypeData(announcementFeature)}
                        value={featureValue}
                        onChange={onFeatureChangedHandler}
                        className={classes.select}
                    />
                )
              }
              topBottom={false}
          />
        </HeaderTemplate>
        <ContentTemplate type="full">
          <Box className={classes.headerContainer}>
            {displaySectionHeaderLabel(featureValue, t)}
            {authorities.data?.includes(
                generateAuthorityName(
                    'CP',
                    FunctionModule.ADMIN,
                    ANNOUNCEMENT_FUNCTION_MAP[featureValue],
                    PermissionType.CREATE
                )
            ) && (
                <Button
                    className={classes.button}
                    fullWidth={isMobile}
                    onClick={handleCreateNewAnnouncement}
                    id="button-new-announcement-broadcast-message-faq"
                >
                  {displayButtonLabel(featureValue, t)}
                </Button>
            )}
          </Box>
          <Box className={classes.dataTable}>
            <MUIDataTable
                isLoading={isLoadingAnnouncementListing}
                state={state}
                dispatch={tableDispatch}
                columns={tableColumns(
                    featureValue,
                    state,
                    announcementConfig as AnnouncementConfigResponse,
                    {
                      classes,
                      history,
                      t,
                    },
                    setDeleteAnnouncementTitleUuid,
                    setOpenDeleteModal,
                    authorities.data
                )}
                data={state.data}
                emptyDataLabel={t('announcementsNotifications->>Listing.EmptyDataTableMessage')}
                options={{
                  ...tOptions,
                  sort: true,
                  pagination: true,
                  sortOrder: {
                    name: state.sortColumn || 'updatedAt',
                    direction: state.order || 'desc',
                  },
                }}
                rowsPerPageOptions={[5, 10, 20, 40, 50]}
            />
          </Box>

          <CustomDialog
              open={openDeleteModal}
              title={t('announcementsNotifications->>Listing.DeleteMessageTitle')}
              message={
                <HtmlTrans
                    i18nKey="announcementsNotifications->>Listing.DeleteMessage"
                    values={{
                      messageType: featureDisplay,
                      title: deleteAnnouncementTitleUuid.title,
                    }}
                />
              }
              action={
                <APIActions
                    onClose={handleCloseDeleteModal}
                    apiCall={handleDelete}
                    apiStatus={deleteAnnouncementStatus}
                    cancelString={t('announcementsNotifications->>Listing.Cancel')}
                    apiString={t('announcementsNotifications->>Listing.Proceed')}
                />
              }
          />
        </ContentTemplate>
      </>
  );
};

export default ManageAnnouncementsListing;
